import React, { useState, useEffect, useRef, useCallback } from "react";
import { ArrowUp, ArrowDown, Copy, Trash2, SlidersHorizontal } from "lucide-react";
import { useBlockStyles } from "../context/BlockStylesContext";
import { useComposerContext } from "../context/ComposerContext";
import DeleteConfirmModal from "../modals/DeleteConfirmModal";

export default function InlineBlockActions({ moveSection, duplicateSection, deleteSection, onOpenAdvancedSettings }) {
  const { activeBlockId, openAdvancedBlock } = useBlockStyles();
  const { setActiveSection } = useComposerContext();
  const [pos, setPos] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const barRef = useRef(null);

  const updatePosition = useCallback(() => {
    if (!activeBlockId) {
      setPos(null);
      return;
    }
    const el = document.querySelector(`[data-bc-id="${activeBlockId}"]`);
    if (!el) {
      setPos(null);
      return;
    }
    const rect = el.getBoundingClientRect();
    const barWidth = 210;
    const calculatedLeft = rect.left + rect.width / 2 - barWidth / 2;
    setPos({
      top: rect.top - 36,
      left: Math.min(Math.max(8, calculatedLeft), window.innerWidth - barWidth - 8),
    });
  }, [activeBlockId]);

  useEffect(() => {
    updatePosition();

    if (!activeBlockId) return;

    const onScroll = () => updatePosition();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [activeBlockId, updatePosition]);

  if (!activeBlockId || !pos || pos.top < 60) return null;

  const ensureActive = () => {
    const el = document.querySelector(`[data-bc-id="${activeBlockId}"]`);
    if (el) setActiveSection(el);
    return el;
  };

  const handleMove = (dir) => {
    const section = ensureActive();
    if (section) moveSection(dir, section);
  };

  const handleDuplicate = () => {
    const section = ensureActive();
    if (section) duplicateSection(section);
  };

  const handleDelete = () => {
    const section = ensureActive();
    if (section) deleteSection(section);
  };

  return (
    <>
      <div
        ref={barRef}
        data-bc-inline-actions
        className="fixed z-[85] bg-white border border-bookish-purple/15 rounded-xl shadow-lg px-1 py-0.5 flex items-center gap-0.5"
        style={{ top: pos.top, left: pos.left }}
      >
        <button
          type="button"
          onClick={() => handleMove("up")}
          className="p-1.5 rounded-lg hover:bg-bookish-purple/8 text-bookish-purple/50 hover:text-bookish-purple transition-colors"
          title="Move up"
        >
          <ArrowUp size={13} />
        </button>
        <button
          type="button"
          onClick={() => handleMove("down")}
          className="p-1.5 rounded-lg hover:bg-bookish-purple/8 text-bookish-purple/50 hover:text-bookish-purple transition-colors"
          title="Move down"
        >
          <ArrowDown size={13} />
        </button>
        <button
          type="button"
          onClick={() => {
            openAdvancedBlock(activeBlockId);
            onOpenAdvancedSettings && onOpenAdvancedSettings(activeBlockId);
          }}
          data-bc-open-advanced
          className="p-1.5 rounded-lg hover:bg-bookish-purple/8 text-bookish-purple/50 hover:text-bookish-purple transition-colors"
          title="Advanced styles"
        >
          <span className="inline-flex items-center gap-1 text-[11px] font-medium px-1"><SlidersHorizontal size={12} />Advanced</span>
        </button>
        <button
          type="button"
          onClick={handleDuplicate}
          className="p-1.5 rounded-lg hover:bg-bookish-purple/8 text-bookish-purple/50 hover:text-bookish-purple transition-colors"
          title="Duplicate"
        >
          <Copy size={13} />
        </button>
        <div className="w-px h-4 bg-bookish-purple/10 mx-0.5" />
        <button
          type="button"
          onClick={() => setDeleteConfirmOpen(true)}
          className="p-1.5 rounded-lg hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors"
          title="Delete"
        >
          <Trash2 size={13} />
        </button>
      </div>

      <DeleteConfirmModal
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
      />
    </>
  );
}
